// src/components/calendar/mergeWorkareaGroups.js
//
// Fusiona los grupos de "workareas" de un mismo calendario (ej. CAJA y
// REPONEDOR, ambos bajo el área MINI en la tabla `position` de la base) en
// un solo grupo virtual, para mostrar una sola tabla por área en vez de una
// tabla separada por sub-área/posición. No toca nada del backend: la
// generación del calendario sigue
// devolviendo un grupo por workarea real (necesario para la limpieza de
// conflictos multirol y la cobertura por workarea), esto solo re-arma esos
// grupos del lado del cliente para la presentación.
//
// Cada empleado de cada día queda con:
//   - workarea_id: el id REAL de la sub-área en la que estaba ese día (no un
//     valor sintético) -- se usa para que al editar un turno el pedido siga
//     apuntando al registro correcto.
//   - area_tag: nombre de la(s) sub-área(s) en la(s) que aparece ese día,
//     unidas con " + " si aparece en más de una (caso multirol).

export function mergeWorkareaGroups(workareaGroups) {
  const groups = Array.isArray(workareaGroups) ? workareaGroups.filter(Boolean) : [];

  if (groups.length === 0) {
    return null;
  }

  if (groups.length === 1) {
    const only = groups[0];
    return {
      ...only,
      workarea_ids: [only.workarea_id],
    };
  }

  const areaNameById = {};
  groups.forEach((g) => { areaNameById[String(g.workarea_id)] = g.workarea_name || `Área ${g.workarea_id}`; });

  const allDates = new Set();
  groups.forEach((g) => Object.keys(g.days || {}).forEach((d) => allDates.add(d)));

  const mergedDays = {};

  allDates.forEach((date) => {
    const employeesByCi = {};
    const mergedCounts = {};
    let dayFlags = null;

    groups.forEach((group) => {
      const dayData = group.days?.[date];
      if (!dayData) return;

      if (!dayFlags) {
        const { employees, workshift_counts, ...rest } = dayData;
        dayFlags = rest;
      }

      Object.entries(dayData.workshift_counts || {}).forEach(([wsId, counts]) => {
        if (!mergedCounts[wsId]) mergedCounts[wsId] = { actual_count: 0, expected_count: 0 };
        mergedCounts[wsId].actual_count += Number(counts?.actual_count || 0);
        mergedCounts[wsId].expected_count += Number(counts?.expected_count ?? counts?.expected ?? 0);
      });

      (dayData.employees || []).forEach((emp) => {
        const ci = String(emp?.ci || '').trim();
        if (!ci) return;

        const areaName = areaNameById[String(group.workarea_id)];
        const existing = employeesByCi[ci];

        if (!existing) {
          employeesByCi[ci] = {
            ...emp,
            workarea_id: group.workarea_id,
            source_workarea_ids: [group.workarea_id],
            area_tag: areaName,
          };
          return;
        }

        const alreadyTagged = existing.source_workarea_ids.includes(group.workarea_id);
        const sourceIds = alreadyTagged
          ? existing.source_workarea_ids
          : [...existing.source_workarea_ids, group.workarea_id];
        const areaTag = sourceIds.map((id) => areaNameById[String(id)] || `Área ${id}`).join(' + ');

        // Si en el grupo que ya teníamos la persona no trabajaba (S/A) pero en
        // este sí, preferimos mostrar el turno real -- pero conservamos el tag
        // combinado de ambas áreas de origen.
        const preferCurrent = !existing.work && emp?.work;

        employeesByCi[ci] = {
          ...(preferCurrent ? emp : existing),
          workarea_id: preferCurrent ? group.workarea_id : existing.workarea_id,
          source_workarea_ids: sourceIds,
          area_tag: areaTag,
        };
      });
    });

    mergedDays[date] = {
      ...(dayFlags || {}),
      employees: Object.values(employeesByCi),
      workshift_counts: mergedCounts,
    };
  });

  const rowOrder = [];
  const seenCi = new Set();
  groups.forEach((g) => {
    (g.row_order || []).forEach((ci) => {
      const key = String(ci);
      if (!seenCi.has(key)) { seenCi.add(key); rowOrder.push(key); }
    });
  });

  const first = groups[0];
  return {
    ...first,
    workarea_id: first.workarea_id,
    workarea_ids: groups.map((g) => g.workarea_id),
    workarea_name: groups.map((g) => g.workarea_name || `Área ${g.workarea_id}`).join(' + '),
    days: mergedDays,
    row_order: rowOrder,
  };
}
